/**
 * Rename a division inside one event.
 *
 * The rename is a cascade: the division name is stored as text in a lot of
 * places (registrations, pools, games, court rules, places, coach pricing,
 * purchased extras, the event's own division list). Missing one of them is
 * silent and damaging — a court allowlist for a name that no longer exists
 * stops applying, and paid extras get orphaned — so the count is shown first,
 * in plain words, and the write is one all-or-nothing operation in the
 * database.
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Pencil, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { invokeEdgeFunction } from '@/lib/invokeEdgeFunction';

interface Props {
  eventId: string;
  division: string;
  disabled?: boolean;
  onDone?: (newName: string) => void;
}

interface Preview {
  sentence: string;
  total: number;
  published_games: number;
  conflict?: boolean;
  tables: Array<{ table: string; label: string; count: number }>;
}

export default function RenameDivisionButton({ eventId, division, disabled, onDone }: Props) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(division);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<Preview | null>(null);

  const call = async (payload: Record<string, unknown>) => {
    const { data, error } = await invokeEdgeFunction('rle-operator-schedule-manual', {
      body: { action: 'rename_division', event_id: eventId, from: division, ...payload },
    });
    if (error) throw error;
    if (!(data as any)?.ok) throw new Error((data as any)?.error?.message ?? 'Something went wrong.');
    return (data as any).data;
  };

  const openDialog = () => {
    setName(division);
    setPreview(null);
    setOpen(true);
  };

  const check = async (to: string) => {
    setBusy(true);
    try {
      setPreview(await call({ to, preview: true }));
    } catch (e: any) {
      toast.error(e?.message ?? 'Could not work out what would change.');
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    const to = name.trim();
    if (!to || to === division) return;
    setBusy(true);
    try {
      const res = await call({ to, confirm: true });
      toast.success(`${division} is now ${to} — ${res.total} row${res.total === 1 ? '' : 's'} updated.`);
      setOpen(false);
      onDone?.(to);
    } catch (e: any) {
      toast.error(e?.message ?? 'Nothing was renamed.');
    } finally {
      setBusy(false);
    }
  };

  const to = name.trim();
  const canPreview = !!to && to !== division;

  return (
    <>
      <Button size="sm" variant="outline" disabled={disabled} onClick={openDialog}>
        <Pencil className="h-3.5 w-3.5 mr-1" /> Rename division
      </Button>

      <AlertDialog open={open} onOpenChange={(v) => { if (!busy) setOpen(v); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Rename {division}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                <p className="text-muted-foreground">
                  The new name replaces {division} everywhere on this event — teams, pools,
                  games, court rules, places, coach pricing and anything already purchased.
                </p>
                <Input
                  value={name}
                  autoFocus
                  disabled={busy}
                  onChange={(e) => { setName(e.target.value); setPreview(null); }}
                  onBlur={() => { if (canPreview) void check(to); }}
                  placeholder="New division name"
                />
                {busy && !preview && (
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" /> Counting what would change…
                  </div>
                )}
                {preview && preview.conflict && (
                  <p className="rounded border border-destructive/40 bg-destructive/10 p-2">
                    {preview.sentence}
                  </p>
                )}
                {preview && !preview.conflict && (
                  <>
                    <p>{preview.sentence}</p>
                    {preview.published_games > 0 && (
                      <p className="rounded border border-amber-300 bg-amber-50 p-2 text-amber-900">
                        This schedule is published. {preview.published_games} game
                        {preview.published_games === 1 ? '' : 's'} coaches can already see carry
                        this division name and will change.
                      </p>
                    )}
                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            {!preview || preview.conflict ? (
              <AlertDialogAction
                disabled={busy || !canPreview}
                onClick={(e) => { e.preventDefault(); void check(to); }}
              >
                {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                Check what changes
              </AlertDialogAction>
            ) : (
              <AlertDialogAction
                disabled={busy || !canPreview}
                onClick={(e) => { e.preventDefault(); void confirm(); }}
              >
                {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                Rename {preview.total} row{preview.total === 1 ? '' : 's'}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
